import React, { useMemo } from 'react';
import moment from 'moment';
import type {
  AfcSubmissionCandidate,
  AfcSubmissionCandidateOpportunity,
  AfcSubmission,
  CandidateRateDiscount,
  User,
  Account,
} from '@axiom/validation';
import type { BadgeBackgroundType } from '@axiom/ui';
import {
  Flashy,
  backgroundColorNames,
  Badge,
  Gutter,
  TilesItem,
  Tiles,
  useBreakpoint,
  CondensedHeader,
  SectionHeader,
  Layout,
  Banner,
  DateUtil,
  CandidateRateDiscountUtil,
  LayoutItem,
  Well,
  FormLabel,
} from '@axiom/ui';
import { useNavigate } from 'react-router-dom';

import { EngagementTalentTile } from '../EngagementTalentTile/EngagementTalentTile';
import { EditEngagementButton } from '../EditEngagementButton/EditEngagementButton';

export type EngagementTalentListProps = {
  hasMultipleRoles?: boolean;
  candidateOpportunities: AfcSubmissionCandidateOpportunity[];
  selectedCandidateId?: AfcSubmissionCandidate['id'];
  submission: AfcSubmission;
  title: string;
  discount: CandidateRateDiscount | null;
  onLoggedOutSubmissionClick: () => void;
  account?: Account;
  user?: User | null;
};

export const BADGE_VALUES = {
  AXIOM_PICK: 'AXIOM PICK',
  SELF_SUBMITTED: 'SELF-SUBMITTED',
  TOP_MATCH: 'TOP MATCH',
  INTERVIEWING: 'INTERVIEWING',
};

const badgeMap = new Map([
  [BADGE_VALUES.AXIOM_PICK, backgroundColorNames.lightOrange],
  [BADGE_VALUES.SELF_SUBMITTED, backgroundColorNames.lightBlue],
  [BADGE_VALUES.TOP_MATCH, backgroundColorNames.orange],
  [BADGE_VALUES.INTERVIEWING, backgroundColorNames.navyBlue],
]);

const setBadgeColor = (badge: string): BadgeBackgroundType => {
  const key = badge.toUpperCase();

  return badgeMap.get(key) || 'gray';
};

export const EngagementTalentList = ({
  hasMultipleRoles = false,
  candidateOpportunities,
  selectedCandidateId,
  submission,
  title,
  discount,
  onLoggedOutSubmissionClick,
  account,
  user,
}: EngagementTalentListProps) => {
  const navigate = useNavigate();
  const { isLargeScreen } = useBreakpoint();
  const { nonPinnedCandidates, pinnedCandidates } =
    candidateOpportunities.reduce<{
      nonPinnedCandidates: EngagementTalentListProps['candidateOpportunities'];
      pinnedCandidates: EngagementTalentListProps['candidateOpportunities'];
    }>(
      (data, candidateOpp) => {
        if (candidateOpp.pinnedByClient) {
          data.pinnedCandidates.push(candidateOpp);
        } else {
          data.nonPinnedCandidates.push(candidateOpp);
        }
        return data;
      },
      { nonPinnedCandidates: [], pinnedCandidates: [] }
    );

  const discountEndingSoon = useMemo(() => {
    if (!discount) return false;

    const now = moment();
    const endDate = moment(discount.applyUntilDate);

    return now.isBetween(
      moment(endDate).subtract(7, 'days'),
      endDate,
      'day',
      '[]'
    );
  }, [discount?.applyUntilDate]);

  const showBadges = (candidateOpp: AfcSubmissionCandidateOpportunity) => {
    if (candidateOpp.badge) {
      return (
        <Badge
          flush="right"
          background={setBadgeColor(candidateOpp.badge)}
          name="badge"
        >
          {candidateOpp.badge}
        </Badge>
      );
    }

    if (candidateOpp.candidate.badges.length > 0) {
      return candidateOpp.candidate.badges.map(
        (badge: AfcSubmissionCandidate['badges'][number]) => {
          return (
            <Badge
              flush="right"
              background={setBadgeColor(badge)}
              name="badge"
              key={badge}
            >
              {badge}
            </Badge>
          );
        }
      );
    }

    return null;
  };

  const renderTileItem = (candidateOpp: AfcSubmissionCandidateOpportunity) => {
    return (
      <TilesItem
        selected={candidateOpp.candidate.id === selectedCandidateId}
        key={candidateOpp.candidate.id}
        onClick={() => {
          navigate(`./${candidateOpp.candidate.id}`);
        }}
        name={`${candidateOpp.candidate.id}-${candidateOpp.pinnedByClient ? 'pinned' : 'matched'}`}
        Badge={showBadges(candidateOpp)}
      >
        <Gutter top={isLargeScreen ? '8px' : null} bottom="8px" left="8px">
          <EngagementTalentTile
            candidateOpp={candidateOpp}
            submission={submission}
          />
        </Gutter>
      </TilesItem>
    );
  };

  return (
    <>
      {discount ? (
        <Gutter bottom="16px">
          <Banner name="DISCOUNT" type="info" impact="high">
            {discountEndingSoon ? (
              <>
                <Flashy name="ENDING_SOON" bold>
                  Offer Ending Soon!
                </Flashy>
                <br />
              </>
            ) : null}
            You are getting a{' '}
            {CandidateRateDiscountUtil.getPercentage(discount)} discount. Select
            talent to Interview by{' '}
            {DateUtil.displayDate(discount.applyUntilDate)} to lock in your
            savings.
          </Banner>
        </Gutter>
      ) : null}
      {!!hasMultipleRoles && (
        <Gutter bottom="16px">
          <SectionHeader name="role-title">{title.toUpperCase()}</SectionHeader>
        </Gutter>
      )}
      <Layout direction="vertical" name={`${title} item`}>
        {pinnedCandidates.length > 0 && (
          <Gutter bottom="24px">
            <Gutter bottom="16px">
              <CondensedHeader name="pinned-talent-header">
                PINNED TALENT
              </CondensedHeader>
            </Gutter>
            <Tiles columnWidth={1} name="pinned-candidate-tiles">
              {pinnedCandidates.map(candidateOpp => {
                return renderTileItem(candidateOpp);
              })}
            </Tiles>
          </Gutter>
        )}
        <Gutter bottom="24px">
          <Gutter bottom="16px">
            {submission.selfService && submission.editable ? (
              <Layout position="space-between">
                <LayoutItem>
                  <CondensedHeader name="pendo-matches-header">
                    MATCHES
                  </CondensedHeader>
                </LayoutItem>
                <LayoutItem position="right">
                  <EditEngagementButton
                    name="edit-engagement"
                    onLoggedOutSubmissionClick={onLoggedOutSubmissionClick}
                    account={account}
                    user={user}
                    pattern="secondary"
                    variation="minimal"
                  />
                </LayoutItem>
              </Layout>
            ) : (
              <CondensedHeader name="matches-header">MATCHES</CondensedHeader>
            )}
          </Gutter>
          <Tiles columnWidth={1} name="matched-candidate-tiles">
            {nonPinnedCandidates.map(candidateOpp => {
              return renderTileItem(candidateOpp);
            })}
          </Tiles>
          {nonPinnedCandidates.length === 0 && (
            <Well background="light" name="empty-candidate-well">
              <FormLabel>No matches</FormLabel>
            </Well>
          )}
        </Gutter>
      </Layout>
    </>
  );
};
